/**
 * Анализ результата диагностики.
 *
 * Из потока нажатий выводятся baseline и слабые места. Единицы формируются на
 * уровне слов и биграмм: отдельная буква редко бывает проблемой сама по себе,
 * проблема обычно в переходе между клавишами.
 */

import type {
  Baseline,
  KeystrokeEvent,
  LanguageCode,
  LayoutId,
  MasteryStat,
  MasteryUnit,
  TypingError,
  TypingMetrics,
} from '@type2x/shared-contracts';
import { emptyStat, toMasteryUnitId } from '@type2x/shared-contracts';
import { median, percentile } from '@type2x/typing-engine';
import { applyObservation, scheduleNextReview, type LearningConfig } from '@type2x/learning-engine';
import { getWords } from '@type2x/corpora';

import type { DiagnosticResult, WeakSpot } from '../contracts/diagnostics.js';
import { judgeLevel } from './level.js';
import { MIN_WEAK_SPOTS } from '../contracts/diagnostics.js';

/** Наблюдения по одному слову за время диагностики. */
interface WordObservation {
  readonly word: string;
  attempts: number;
  errors: number;
  corrections: number;
  latencies: number[];
}

function frequencyOf(language: LanguageCode, word: string): number {
  return getWords(language).find((entry) => entry.text === word)?.frequency ?? 0;
}

/** Разбирает поток нажатий по словам задания. */
function collectWordObservations(
  words: readonly string[],
  events: readonly KeystrokeEvent[],
  errors: readonly TypingError[],
): WordObservation[] {
  const observations = new Map<string, WordObservation>();
  const errorPositions = new Set(errors.map((error) => error.position));

  let position = 0;
  for (const word of words) {
    const existing = observations.get(word) ?? {
      word,
      attempts: 0,
      errors: 0,
      corrections: 0,
      latencies: [],
    };

    for (let offset = 0; offset < [...word].length; offset += 1) {
      const event = events[position + offset];
      if (event === undefined) continue;
      if (event.interKeyIntervalMs > 0) existing.latencies.push(event.interKeyIntervalMs);
      if (errorPositions.has(position + offset)) existing.errors += 1;
      if (event.corrected) existing.corrections += 1;
    }

    existing.attempts += 1;
    observations.set(word, existing);
    // Плюс один символ на пробел между словами.
    position += [...word].length + 1;
  }

  return [...observations.values()];
}

function severityOf(stat: MasteryStat, config: LearningConfig): number {
  const errorPart = stat.errorRate;
  const hesitationPart = Math.min(stat.medianLatencyMs / config.hesitationCeilingMs, 1);
  return Math.min(errorPart * 0.65 + hesitationPart * 0.35, 1);
}

function reasonFor(stat: MasteryStat, config: LearningConfig): WeakSpot['reason'] {
  if (stat.errorRate > 0) return 'errors';
  if (stat.correctionRate > 0) return 'corrections';
  return stat.medianLatencyMs > config.hesitationThresholdMs ? 'hesitation' : 'hesitation';
}

/** Строит учебную единицу для слова диагностики. */
function unitFor(word: string, language: LanguageCode, layoutId: LayoutId): MasteryUnit {
  return {
    id: toMasteryUnitId(`word:${word}`),
    kind: 'word',
    content: word,
    language,
    layoutId,
    realWorldFrequency: frequencyOf(language, word),
  };
}

/** Строит статистику единицы по наблюдениям диагностики. */
function statFor(
  unit: MasteryUnit,
  observation: WordObservation,
  config: LearningConfig,
  nowIso: string,
  nowMs: number,
): MasteryStat {
  const characterCount = [...observation.word].length * observation.attempts;
  const updated = applyObservation(
    emptyStat(unit.id, unit.language, unit.layoutId, nowIso),
    {
      attempts: Math.max(characterCount, 1),
      errors: observation.errors,
      firstTryCorrect: Math.max(characterCount - observation.errors, 0),
      corrections: observation.corrections,
      medianLatencyMs: median(observation.latencies),
      p95LatencyMs: percentile(observation.latencies, 0.95),
    },
    config,
    nowIso,
  );

  return { ...updated, nextReviewAt: scheduleNextReview(updated, config, nowMs) };
}

/**
 * Строит итог диагностики.
 *
 * Если явных ошибок мало, слабыми считаются самые медленные слова: у любого
 * пользователя есть относительно слабые места, и обучение должно с чего-то
 * начаться (ТЗ §16.3, шаг 6).
 */
export function analyzeDiagnostic(
  language: LanguageCode,
  layoutId: LayoutId,
  words: readonly string[],
  events: readonly KeystrokeEvent[],
  errors: readonly TypingError[],
  metrics: TypingMetrics,
  config: LearningConfig,
  nowIso: string,
): DiagnosticResult {
  const nowMs = Date.parse(nowIso);
  const observations = collectWordObservations(words, events, errors);

  const units: MasteryUnit[] = [];
  const stats: MasteryStat[] = [];

  for (const observation of observations) {
    const unit = unitFor(observation.word, language, layoutId);
    units.push(unit);
    stats.push(statFor(unit, observation, config, nowIso, nowMs));
  }

  const byUnitId = new Map(units.map((unit) => [unit.id, unit]));
  const weakSpots: WeakSpot[] = stats
    .map((stat) => ({
      unit: byUnitId.get(stat.unitId)!,
      stat,
      reason: reasonFor(stat, config),
      severity: severityOf(stat, config),
    }))
    .sort((a, b) => b.severity - a.severity || a.unit.id.localeCompare(b.unit.id))
    .slice(0, Math.max(MIN_WEAK_SPOTS, Math.ceil(stats.length * 0.25)));

  const baseline: Baseline = {
    language,
    layoutId,
    wpm: metrics.wpm,
    cpm: metrics.cpm,
    accuracy: metrics.accuracy,
    measuredAt: nowIso,
  };

  return {
    baseline,
    metrics,
    level: judgeLevel(metrics, events, config),
    weakSpots,
    units,
    stats,
  };
}
